// src/pages/AdminPanelPage.js
import React, { useEffect, useState, useCallback } from "react";
import {
  getAllUsers,
  getAllGpts,
  grantGptAccess,
  revokeGptAccess,
  createGpt,
  updateGpt,
  deleteGpt,
  adminChangeUserPassword,
  getUserGptsByAdmin,
  createUserByAdmin,
  getUsersWithExpiration,
  getUsersExpiringSoon,
} from "../services/api";
import ExpirationManagementModal from "../components/ExpirationManagementModal";
import styles from "../css/shared.module.css";
import NotificationToast from "../components/NotificationToast";
import UserAutocomplete from "../components/UserAutocomplete";
import QuickAccessPanel from "../components/QuickAccessPanel";
import {
  FiPlus,
  FiLock,
  FiCheck,
  FiX,
  FiEye,
  FiEyeOff,
  FiTrash2,
  FiRefreshCw,
  FiUsers,
  FiEdit,
  FiSettings,
} from "react-icons/fi";

const AdminPanelPage = () => {
  // --- Estados del componente ---
  const [users, setUsers] = useState([]);
  const [gpts, setGpts] = useState([]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [notification, setNotification] = useState({
    show: false,
    text: "",
    type: "",
  });
  const [isSubmittingAccess, setIsSubmittingAccess] = useState(false);
  const [isSubmittingGpt, setIsSubmittingGpt] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [isSubmittingUser, setIsSubmittingUser] = useState(false);
  const [isEditingGpt, setIsEditingGpt] = useState(false);
  const [editingGptId, setEditingGptId] = useState(null);
  const [accessUser, setAccessUser] = useState("");
  const [accessGpt, setAccessGpt] = useState("");
  const [newGpt, setNewGpt] = useState({
    name: "",
    description: "",
    openAiAssistantId: "",
    brainContextChat: false,
    brainContextMessage: false,
  });
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "user",
    accessExpirationDate: "",
  });
  const [showNewUserPass, setShowNewUserPass] = useState({ password: false, confirm: false });
  const [passwordUser, setPasswordUser] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPass, setShowPass] = useState({ new: false, confirm: false });
  const [selectedUserForGpts, setSelectedUserForGpts] = useState("");
  const [userGptsList, setUserGptsList] = useState([]);
  const [isUserGptsLoading, setIsUserGptsLoading] = useState(false);
  const [activePanel, setActivePanel] = useState("gpt-management");
  
  // Estados para gestión de expiraciones
  const [expirationStats, setExpirationStats] = useState({
    expiringSoon: 0,
    expired: 0,
    total: 0
  });
  const [isLoadingExpirationData] = useState(false);
  
  // Estados para el modal de gestión de expiraciones
  const [isExpirationModalOpen, setIsExpirationModalOpen] = useState(false);
  const [expirationModalTab, setExpirationModalTab] = useState("expiring");

  // Función helper para mostrar notificaciones
  const showNotification = (text, type) => {
    setNotification({ show: true, text, type });
  };

  // Función para manejar la selección de paneles
  const handlePanelSelect = (panelId) => {
    setActivePanel(panelId);
    
    // Si es el panel de gestión de expiraciones, abrir el modal
    if (panelId === "expiration-management") {
      setExpirationModalTab("all");
      setIsExpirationModalOpen(true);
      return;
    }
    
    // Mapear los nuevos IDs a los IDs existentes para compatibilidad
    const panelMapping = {
      "gpt-management": "manage-gpts",
      "gpt-access": "access",
      "user-management": "create-user",
      "user-access": "view-access",
    };
    
    const actualPanelId = panelMapping[panelId] || panelId;
    
    // Hacer scroll al panel seleccionado
    const panelElement = document.getElementById(`panel-${actualPanelId}`);
    if (panelElement) {
      panelElement.scrollIntoView({ behavior: "smooth", block: "start" });
      
      // Efecto visual de resaltado
      panelElement.style.transition = 'all 0.3s ease';
      panelElement.style.transform = 'scale(1.02)';
      panelElement.style.boxShadow = '0 8px 25px -5px rgba(59, 130, 246, 0.3)';
      
      setTimeout(() => {
        panelElement.style.transform = 'scale(1)';
        panelElement.style.boxShadow = '0 2px 4px -1px rgba(0, 0, 0, 0.1)';
      }, 1000);
    }
  };

  // --- Lógica de fetching ---
  const fetchData = useCallback(async () => {
    let usersRes, gptsRes, expiringRes, allUsersRes;
    
    try {
      console.log("🔄 Cargando datos iniciales...");
      
      [usersRes, gptsRes, expiringRes, allUsersRes] = await Promise.all([
        getAllUsers(),
        getAllGpts(),
        getUsersExpiringSoon(),
        getUsersWithExpiration(),
      ]);
      
      console.log("📊 Resultados de las consultas:", {
        users: usersRes.success ? usersRes.data.length : usersRes.message,
        gpts: gptsRes.success ? gptsRes.data.length : gptsRes.message,
        expiring: expiringRes.success ? expiringRes.data.length : expiringRes.message,
        allUsers: allUsersRes.success ? allUsersRes.data.length : allUsersRes.message
      });
      
      if (usersRes.success) setUsers(usersRes.data);
      if (gptsRes.success) setGpts(gptsRes.data);
      
      // Cargar estadísticas de expiración
      if (expiringRes.success) {
        setExpirationStats(prev => ({ ...prev, expiringSoon: expiringRes.data.length }));
      }
      
      if (allUsersRes.success) {
        const expiredCount = allUsersRes.data.filter(user => user.isAccessExpired).length;
        setExpirationStats(prev => ({ 
          ...prev, 
          expired: expiredCount,
          total: allUsersRes.data.length 
        }));
      }
      
      console.log("✅ Datos cargados exitosamente");
    } catch (error) {
      console.error("❌ Error al cargar datos iniciales:", error);
      
      // Mostrar errores específicos para cada API
      if (usersRes && !usersRes.success) {
        console.error("❌ Error en getAllUsers:", usersRes.message);
      }
      if (gptsRes && !gptsRes.success) {
        console.error("❌ Error en getAllGpts:", gptsRes.message);
      }
      if (expiringRes && !expiringRes.success) {
        console.error("❌ Error en getUsersExpiringSoon:", expiringRes.message);
      }
      if (allUsersRes && !allUsersRes.success) {
        console.error("❌ Error en getUsersWithExpiration:", allUsersRes.message);
      }
      
      showNotification(`Error al cargar los datos iniciales: ${error.message}`, "error");
    } finally {
      setIsPageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listener para abrir el modal desde el menú del header
  useEffect(() => {
    const handleOpenExpirationModal = (event) => {
      const { tab } = event.detail;
      setExpirationModalTab(tab || "all");
      setIsExpirationModalOpen(true);
    };

    window.addEventListener('openExpirationModal', handleOpenExpirationModal);
    
    return () => {
      window.removeEventListener('openExpirationModal', handleOpenExpirationModal);
    };
  }, []);

  // Manejar scroll automático cuando se accede con anchors
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const panelId = hash.replace('#', '');
      const panelElement = document.getElementById(`panel-${panelId}`);
      if (panelElement) {
        setTimeout(() => {
          panelElement.scrollIntoView({ behavior: "smooth", block: "start" });
          setActivePanel(panelId);
        }, 500); // Pequeño delay para asegurar que la página esté cargada
      }
    }
  }, []);

  // --- Funciones de GPT ---
  const handleCreateGpt = async (e) => {
    e.preventDefault();
    setIsSubmittingGpt(true);
    const res = await createGpt(newGpt);
    const messageText = res.success ? "GPT creado con éxito." : res.message;
    showNotification(messageText, res.success ? "success" : "error");
    if (res.success) {
      setNewGpt({ 
        name: "", 
        description: "", 
        openAiAssistantId: "",
        brainContextChat: false,
        brainContextMessage: false
      });
      await fetchData();
      
      // Disparar evento para actualizar Header y otros componentes
      console.log("🔄 [AdminPanel] GPT creado, disparando evento de actualización...");
      window.dispatchEvent(new CustomEvent('gptsUpdated'));
    }
    setIsSubmittingGpt(false);
  };

  const handleEditGpt = (gpt) => {
    setEditingGptId(gpt._id);
    setIsEditingGpt(true);
    setNewGpt({
      name: gpt.name,
      description: gpt.description,
      openAiAssistantId: gpt.openAiAssistantId,
      brainContextChat: gpt.brainContextChat || false,
      brainContextMessage: gpt.brainContextMessage || false,
    });
  };

  const handleUpdateGpt = async (e) => {
    e.preventDefault();
    setIsSubmittingGpt(true);
    const res = await updateGpt(editingGptId, newGpt);
    const messageText = res.success ? "GPT actualizado con éxito." : res.message;
    showNotification(messageText, res.success ? "success" : "error");
    if (res.success) {
      setNewGpt({ 
        name: "", 
        description: "", 
        openAiAssistantId: "",
        brainContextChat: false,
        brainContextMessage: false
      });
      setIsEditingGpt(false);
      setEditingGptId(null);
      await fetchData();
    }
    setIsSubmittingGpt(false);
  };

  const handleDeleteGpt = async (gptId) => {
    if (!window.confirm("¿Estás seguro de que quieres eliminar este GPT? Esta acción no se puede deshacer.")) {
      return;
    }
    
    const res = await deleteGpt(gptId);
    const messageText = res.success ? "GPT eliminado con éxito." : res.message;
    showNotification(messageText, res.success ? "success" : "error");
    if (res.success) {
      await fetchData();
      
      // Disparar evento para actualizar Header y otros componentes
      console.log("🔄 [AdminPanel] GPT eliminado, disparando evento de actualización...");
      window.dispatchEvent(new CustomEvent('gptsUpdated'));
    }
  };

  const handleCancelEdit = () => {
    setIsEditingGpt(false);
    setEditingGptId(null);
    setNewGpt({ 
      name: "", 
      description: "", 
      openAiAssistantId: "",
      brainContextChat: false,
      brainContextMessage: false
    });
  };

  // --- Funciones de Acceso ---
  const handleGrantAccess = async () => {
    setIsSubmittingAccess(true);
    const res = await grantGptAccess(accessUser, accessGpt);
    const messageText = res.success
      ? "Acceso otorgado correctamente."
      : res.message;
    showNotification(messageText, res.success ? "success" : "error");
    setIsSubmittingAccess(false);
  };

  const handleRevokeAccess = async () => {
    setIsSubmittingAccess(true);
    const res = await revokeGptAccess(accessUser, accessGpt);
    const messageText = res.success
      ? "Acceso revocado correctamente."
      : res.message;
    showNotification(messageText, res.success ? "success" : "error");
    setIsSubmittingAccess(false);
  };

  // --- Funciones de Usuario ---
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setIsSubmittingUser(true);
    
    if (newUser.password !== newUser.confirmPassword) {
      showNotification("Las contraseñas no coinciden.", "error");
      setIsSubmittingUser(false);
      return;
    }

    const userData = {
      username: newUser.username,
      email: newUser.email,
      password: newUser.password,
      role: newUser.role,
      accessExpirationDate: newUser.accessExpirationDate,
    };

    const res = await createUserByAdmin(userData);
    showNotification(res.message, res.success ? "success" : "error");
    
    if (res.success) {
      setNewUser({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        role: "user",
        accessExpirationDate: "",
      });
      await fetchData(); // Recargar la lista de usuarios
      
      // Disparar evento para actualizar Header y otros componentes
      console.log("🔄 [AdminPanel] Usuario creado, disparando evento de actualización...");
      window.dispatchEvent(new CustomEvent('gptsUpdated'));
    }
    
    setIsSubmittingUser(false);
  };

  const handleAdminChangePassword = async (e) => {
    e.preventDefault();
    setIsSubmittingPassword(true);
    if (newPassword !== confirmPassword) {
      showNotification("Las contraseñas no coinciden.", "error");
      setIsSubmittingPassword(false);
      return;
    }

    const res = await adminChangeUserPassword(passwordUser, newPassword);
    showNotification(res.message, res.success ? "success" : "error");
    
    if (res.success) {
      setPasswordUser("");
      setNewPassword("");
      setConfirmPassword("");
    }
    
    setIsSubmittingPassword(false);
  };

  // --- Funciones de Gestión de Expiraciones ---
  const refreshExpirationStats = async () => {
    try {
      const [expiringRes, allUsersRes] = await Promise.all([
        getUsersExpiringSoon(),
        getUsersWithExpiration(),
      ]);
      
      if (expiringRes.success) {
        setExpirationStats(prev => ({ ...prev, expiringSoon: expiringRes.data.length }));
      }
      
      if (allUsersRes.success) {
        const expiredCount = allUsersRes.data.filter(user => user.isAccessExpired).length;
        setExpirationStats(prev => ({ 
          ...prev, 
          expired: expiredCount,
          total: allUsersRes.data.length 
        }));
      }
    } catch (error) {
      console.error("Error refreshing expiration stats:", error);
    }
  };

  const handleViewExpiringSoon = () => {
    setExpirationModalTab("expiring");
    setIsExpirationModalOpen(true);
  };

  const handleViewAllUsers = () => {
    setExpirationModalTab("all");
    setIsExpirationModalOpen(true);
  };

  const handleCloseExpirationModal = () => {
    setIsExpirationModalOpen(false);
    // Recargar estadísticas después de cerrar el modal
    refreshExpirationStats();
  };

  // --- Funciones de Ver Accesos ---
  const handleUserSelection = async (userId) => {
    setSelectedUserForGpts(userId);
    if (userId) {
      setIsUserGptsLoading(true);
      const res = await getUserGptsByAdmin(userId);
      if (res.success) {
        setUserGptsList(res.data);
      } else {
        showNotification(res.message, "error");
        setUserGptsList([]);
      }
      setIsUserGptsLoading(false);
    } else {
      setUserGptsList([]);
    }
  };

  const handleRevokeFromTable = async (userId, gptId, accessId) => {
    const res = await revokeGptAccess(userId, gptId);
    const messageText = res.success
      ? "Acceso revocado correctamente desde la tabla."
      : res.message;
    showNotification(messageText, res.success ? "success" : "error");
    
    if (res.success) {
      // Recargar la lista de GPTs del usuario
      await handleUserSelection(userId);
    }
  };

  if (isPageLoading) {
    return (
      <div>
        <h1 className={styles.title}>Panel de Administración</h1>
        <p className={styles.subtitle}>Cargando datos, por favor espera...</p>
      </div>
    );
  }

  return (
    <div>
      <NotificationToast
        notification={notification}
        onClose={() => setNotification({ ...notification, show: false })}
      />
      <h1 className={styles.title}>Panel de Administración</h1>
      <p className={styles.subtitle}>
        Gestiona usuarios, GPTs y permisos desde aquí.
      </p>
      
      {/* Panel de Quick Access */}
      <QuickAccessPanel 
        onPanelSelect={handlePanelSelect}
        activePanel={activePanel}
      />
      
      {/* Sección de Gestión de GPTs */}
      <div className={styles.panelSection}>
        <h2 className={styles.sectionTitle}>
          <FiSettings className={styles.sectionIcon} />
          Gestión de GPTs
        </h2>
        <div className={styles.grid}>
          {/* --- Tarjeta 1: Gestionar Acceso a GPTs --- */}
          <div id="panel-access" className={styles.card}>
            <h3 className={styles.cardTitle}>Gestionar Acceso a GPTs</h3>
            <form>
              <div className={styles.formGroup}>
                <label htmlFor="accessUser">Usuario</label>
                <UserAutocomplete
                  users={users}
                  value={accessUser}
                  onChange={setAccessUser}
                  placeholder="Buscar usuario..."
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="accessGpt">GPT</label>
                <select
                  id="accessGpt"
                  className={styles.input}
                  onChange={(e) => setAccessGpt(e.target.value)}
                  value={accessGpt}
                >
                  <option value="">-- Seleccionar GPT --</option>
                  {gpts.map((gpt) => (
                    <option key={gpt._id} value={gpt._id}>
                      {gpt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.buttonGroup}>
                <button
                  type="button"
                  className={styles.button}
                  onClick={handleGrantAccess}
                  disabled={!accessUser || !accessGpt || isSubmittingAccess}
                >
                  {isSubmittingAccess ? (
                    <div className={styles.spinner} />
                  ) : (
                    <>
                      <FiCheck /> <span>Otorgar</span>
                    </>
                  )}
                </button>
                <button
                  className={`${styles.button} ${styles.danger}`}
                  onClick={handleRevokeAccess}
                  disabled={!accessUser || !accessGpt || isSubmittingAccess}
                >
                  {isSubmittingAccess ? (
                    <div className={styles.spinner} />
                  ) : (
                    <>
                      <FiX /> <span>Revocar</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* --- Tarjeta 2: Crear/Editar GPT --- */}
          <div id="panel-create-gpt" className={styles.card}>
            <h3 className={styles.cardTitle}>
              {isEditingGpt ? "Editar GPT" : "Crear Nuevo GPT"}
            </h3>
            <form onSubmit={isEditingGpt ? handleUpdateGpt : handleCreateGpt}>
              <div className={styles.formGroup}>
                <label htmlFor="gptName">Nombre del GPT</label>
                <input
                  id="gptName"
                  className={styles.input}
                  type="text"
                  placeholder="Mi Asistente de IA"
                  value={newGpt.name}
                  onChange={(e) => setNewGpt({ ...newGpt, name: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="gptDesc">Descripción</label>
                <input
                  id="gptDesc"
                  className={styles.input}
                  type="text"
                  placeholder="Ayuda con tareas de marketing"
                  value={newGpt.description}
                  onChange={(e) =>
                    setNewGpt({ ...newGpt, description: e.target.value })
                  }
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="gptId">OpenAI Assistant ID</label>
                <input
                  id="gptId"
                  className={styles.input}
                  type="text"
                  placeholder="asst_xxxxxxxxxxxxxxxx"
                  value={newGpt.openAiAssistantId}
                  onChange={(e) =>
                    setNewGpt({ ...newGpt, openAiAssistantId: e.target.value })
                  }
                />
              </div>
              
              <div className={styles.sectionLabel}>Configuración de Brain Context</div>
              <div className={styles.checkboxGroup}>
                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id="brainContextChat"
                    checked={newGpt.brainContextChat}
                    onChange={(e) =>
                      setNewGpt({ ...newGpt, brainContextChat: e.target.checked })
                    }
                    className={styles.checkbox}
                  />
                  <label htmlFor="brainContextChat" className={styles.checkboxLabel}>
                    Adicionar brain context a nivel de chat
                  </label>
                </div>
                <div className={styles.checkboxItem}>
                  <input
                    type="checkbox"
                    id="brainContextMessage"
                    checked={newGpt.brainContextMessage}
                    onChange={(e) =>
                      setNewGpt({ ...newGpt, brainContextMessage: e.target.checked })
                    }
                    className={styles.checkbox}
                  />
                  <label htmlFor="brainContextMessage" className={styles.checkboxLabel}>
                    Adicionar brain context a nivel de mensaje
                  </label>
                </div>
              </div>

              <div className={styles.buttonGroup}>
                <button
                  type="submit"
                  className={styles.button}
                  disabled={
                    !newGpt.name || !newGpt.openAiAssistantId || isSubmittingGpt
                  }
                >
                  {isSubmittingGpt ? (
                    <div className={styles.spinner} />
                  ) : (
                    <>
                      {isEditingGpt ? (
                        <>
                          <FiCheck /> <span>Actualizar GPT</span>
                        </>
                      ) : (
                        <>
                          <FiPlus /> <span>Crear GPT</span>
                        </>
                      )}
                    </>
                  )}
                </button>
                {isEditingGpt && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className={`${styles.button} ${styles.secondary}`}
                    style={{ marginLeft: "10px" }}
                  >
                    <FiX /> <span>Cancelar</span>
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* --- Tarjeta 3: Gestión de GPTs (Ancho completo) --- */}
          <div id="panel-manage-gpts" className={`${styles.card} ${styles.fullWidth}`}>
            <h3 className={styles.cardTitle}>Gestión de GPTs</h3>
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Assistant ID</th>
                    <th>Brain Context</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {gpts.length > 0 ? (
                    gpts.map((gpt) => (
                      <tr key={gpt._id}>
                        <td>{gpt.name}</td>
                        <td>{gpt.description}</td>
                        <td>{gpt.openAiAssistantId}</td>
                        <td>
                          <div className={styles.brainContextInfo}>
                            <span className={gpt.brainContextChat ? styles.enabled : styles.disabled}>
                              Chat: {gpt.brainContextChat ? "Sí" : "No"}
                            </span>
                            <span className={gpt.brainContextMessage ? styles.enabled : styles.disabled}>
                              Mensaje: {gpt.brainContextMessage ? "Sí" : "No"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.actionButtons}>
                            <button
                              onClick={() => handleEditGpt(gpt)}
                              className={`${styles.actionButton} ${styles.edit}`}
                              title="Editar GPT"
                            >
                              <FiEdit size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteGpt(gpt._id)}
                              className={`${styles.actionButton} ${styles.delete}`}
                              title="Eliminar GPT"
                            >
                              <FiTrash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">No hay GPTs creados.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Sección de Gestión de Usuarios */}
      <div className={styles.panelSection}>
        <h2 className={styles.sectionTitle}>
          <FiUsers className={styles.sectionIcon} />
          Gestión de Usuarios
        </h2>
        <div className={styles.grid}>
          {/* --- Tarjeta 1: Crear Usuario --- */}
          <div id="panel-create-user" className={styles.card}>
            <h3 className={styles.cardTitle}>Crear Nuevo Usuario</h3>
            <form onSubmit={handleCreateUser}>
              <div className={styles.formGroup}>
                <label htmlFor="newUsername">Nombre de Usuario</label>
                <input
                  id="newUsername"
                  className={styles.input}
                  type="text"
                  placeholder="usuario123"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserEmail">Correo Electrónico</label>
                <input
                  id="newUserEmail"
                  className={styles.input}
                  type="email"
                  placeholder="usuario@ejemplo.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserPassword">Contraseña</label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="newUserPassword"
                    className={styles.input}
                    type={showNewUserPass.password ? "text" : "password"}
                    placeholder="••••••••"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeIcon}
                    onClick={() => setShowNewUserPass((p) => ({ ...p, password: !p.password }))}
                  >
                    {showNewUserPass.password ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserConfirmPassword">Confirmar Contraseña</label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="newUserConfirmPassword"
                    className={styles.input}
                    type={showNewUserPass.confirm ? "text" : "password"}
                    placeholder="••••••••"
                    value={newUser.confirmPassword}
                    onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className={styles.eyeIcon}
                    onClick={() => setShowNewUserPass((p) => ({ ...p, confirm: !p.confirm }))}
                  >
                    {showNewUserPass.confirm ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserRole">Rol</label>
                <select
                  id="newUserRole"
                  className={styles.input}
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                >
                  <option value="user">Usuario</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newUserExpiration">Fecha de Expiración de Acceso</label>
                <input
                  id="newUserExpiration"
                  className={styles.input}
                  type="date"
                  value={newUser.accessExpirationDate}
                  onChange={(e) => setNewUser({ ...newUser, accessExpirationDate: e.target.value })}
                  required
                />
              </div>
              <div className={styles.buttonGroup}>
                <button
                  type="submit"
                  className={styles.button}
                  disabled={
                    !newUser.username ||
                    !newUser.email ||
                    !newUser.password ||
                    !newUser.accessExpirationDate ||
                    newUser.password !== newUser.confirmPassword ||
                    isSubmittingUser
                  }
                >
                  {isSubmittingUser ? (
                    <div className={styles.spinner} />
                  ) : (
                    <>
                      <FiPlus /> <span>Crear Usuario</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* --- Tarjeta 2: Cambiar Contraseña --- */}
          <div id="panel-change-password" className={styles.card}>
            <h3 className={styles.cardTitle}>Cambiar Contraseña de Usuario</h3>
            <form onSubmit={handleAdminChangePassword}>
              <div className={styles.formGroup}>
                <label htmlFor="passwordUser">Usuario</label>
                <UserAutocomplete
                  users={users}
                  value={passwordUser}
                  onChange={setPasswordUser}
                  placeholder="Buscar usuario..."
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="newPass">Nueva Contraseña</label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="newPass"
                    className={styles.input}
                    type={showPass.new ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className={styles.eyeIcon}
                    onClick={() => setShowPass((p) => ({ ...p, new: !p.new }))}
                  >
                    {showPass.new ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="confirmPass">Confirmar Nueva Contraseña</label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="confirmPass"
                    className={styles.input}
                    type={showPass.confirm ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className={styles.eyeIcon}
                    onClick={() => setShowPass((p) => ({ ...p, confirm: !p.confirm }))}
                  >
                    {showPass.confirm ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
              <div className={styles.buttonGroup}>
                <button
                  type="submit"
                  className={styles.button}
                  disabled={
                    !passwordUser ||
                    !newPassword ||
                    !confirmPassword ||
                    newPassword !== confirmPassword ||
                    isSubmittingPassword
                  }
                >
                  {isSubmittingPassword ? (
                    <div className={styles.spinner} />
                  ) : (
                    <>
                      <FiLock /> <span>Cambiar Contraseña</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* --- Tarjeta 3: Ver Accesos de Usuario --- */}
          <div id="panel-view-access" className={styles.card}>
            <h3 className={styles.cardTitle}>Ver Accesos de Usuario</h3>
            <div className={styles.formGroup}>
              <label htmlFor="selectedUserForGpts">Seleccionar Usuario</label>
              <UserAutocomplete
                users={users}
                value={selectedUserForGpts}
                onChange={handleUserSelection}
                placeholder="Buscar usuario para ver sus GPTs..."
              />
            </div>
            {selectedUserForGpts && (
              <div className={styles.tableContainer}>
                <h4>GPTs Asignados a este Usuario</h4>
                {isUserGptsLoading ? (
                  <p>Cargando GPTs del usuario...</p>
                ) : userGptsList.length > 0 ? (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>GPT</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userGptsList.map((access) => (
                        <tr key={access._id}>
                          <td>
                            {gpts.find((gpt) => gpt._id === access.gptId)?.name ||
                              "GPT no encontrado"}
                          </td>
                          <td>
                            <button
                              className={`${styles.button} ${styles.danger}`}
                              onClick={() =>
                                handleRevokeFromTable(
                                  selectedUserForGpts,
                                  access.gptId,
                                  access._id
                                )
                              }
                              disabled={!access.gptId}
                            >
                              <FiTrash2 size={14} />
                              <span>Revocar</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>Este usuario no tiene GPTs asignados.</p>
                )}
              </div>
            )}
          </div>

          {/* --- Tarjeta 4: Gestión de Expiraciones --- */}
          <div id="panel-expiration-management" className={styles.card}>
            <h3 className={styles.cardTitle}>Gestión de Expiraciones de Acceso</h3>
            <div className={styles.expirationStats}>
              <div className={styles.statCard}>
                <h4>Usuarios que Expiran Pronto</h4>
                <p className={styles.statNumber}>{expirationStats.expiringSoon}</p>
                <p className={styles.statLabel}>Próximos 7 días</p>
              </div>
              <div className={styles.statCard}>
                <h4>Usuarios Expirados</h4>
                <p className={styles.statNumber}>{expirationStats.expired}</p>
                <p className={styles.statLabel}>Sin acceso</p>
              </div>
              <div className={styles.statCard}>
                <h4>Total de Usuarios</h4>
                <p className={styles.statNumber}>{expirationStats.total}</p>
                <p className={styles.statLabel}>Con fecha de expiración</p>
              </div>
            </div>
            <div className={styles.expirationActions}>
              <button 
                className={styles.button} 
                onClick={handleViewExpiringSoon}
                disabled={isLoadingExpirationData}
              >
                <FiRefreshCw /> 
                {isLoadingExpirationData ? "Cargando..." : "Ver Usuarios que Expiran Pronto"}
              </button>
              <button 
                className={styles.button} 
                onClick={handleViewAllUsers}
                disabled={isLoadingExpirationData}
              >
                <FiUsers /> 
                {isLoadingExpirationData ? "Cargando..." : "Ver Todos los Usuarios"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Gestión de Expiraciones */}
      <ExpirationManagementModal
        isOpen={isExpirationModalOpen}
        onClose={handleCloseExpirationModal}
        initialTab={expirationModalTab}
      />
    </div>
  );
};

export default AdminPanelPage;